import type {
	AdvancedMegaMenuHeaderRuntimePayload,
	AdvancedMegaMenuNavIcon,
	GroupHeaderBehavior,
	GroupHeaderBrand,
	GroupHeaderBreakpoints,
	GroupHeaderDenseGroup,
	GroupHeaderDesignTokens,
	GroupHeaderFeaturedCard,
	GroupHeaderGroupMoreLink,
	GroupHeaderMegaPanelLayout,
	GroupHeaderPanelConfig,
	GroupHeaderRegion,
	GroupHeaderStickyMode,
	GroupMegaMenuHeaderPayloadPatch,
	GroupMegaMenuHeaderRuntimePayload,
	MegaMenuHeaderLanguageSwitcher,
	MenuRootNode,
} from "@multi-tenant-cms/types/menu";
import {
	GROUP_HEADER_THEME_TOKEN_DEFAULTS,
	isInheritedGroupHeaderColor,
	LEGACY_GROUP_HEADER_HEX,
	mergeGroupMegaMenuHeaderPayload,
	parseGroupHeaderMegaPanelLayout,
	resolveGroupHeaderColor,
	resolveGroupHeaderDesign,
	sortMenuMegaGroups,
} from "@multi-tenant-cms/types/menu";
import type { AdvancedMegaMenuHeaderElement } from "../types";

export type ReplicaBrand = GroupHeaderBrand;

type DeepPartial<T> = {
	[K in keyof T]?: T[K] extends Array<infer U>
		? U[]
		: T[K] extends object
			? DeepPartial<T[K]>
			: T[K];
};

/**
 * Persisted replica `group` JSON — nested keys are optional until merge.
 * `design` mirrors {@link GroupMegaMenuHeaderPayloadPatch}: `null` means the
 * author explicitly cleared it back to theme defaults (handled by
 * `mergeGroupHeaderDesign`), which a bare `DeepPartial` cannot express.
 */
export type ReplicaChromeConfig = Omit<
	DeepPartial<GroupMegaMenuHeaderRuntimePayload>,
	"design"
> & {
	design?: DeepPartial<GroupHeaderDesignTokens> | null;
};

export const GROUP_PANEL_LAYOUTS: readonly GroupHeaderMegaPanelLayout[] = [
	"dense_columns",
	"dense_columns_featured",
	"product_cards_groups",
	"dense_columns_deadlines",
	"left_tabs",
];

/** Chrome layout for `advanced_mega_menu_header` — replica is the group-dense header. */
export type AdvancedMegaMenuChromeVariant = "classic" | "replica";

/** Advanced runtime plus optional group/replica JSON (types package cannot extend yet). */
export type AdvancedMegaMenuRuntimeWithGroup =
	AdvancedMegaMenuHeaderRuntimePayload & {
		chromeVariant?: AdvancedMegaMenuChromeVariant;
		group?: GroupMegaMenuHeaderRuntimePayload;
	};

export function normalizeChromeVariant(
	value: unknown
): AdvancedMegaMenuChromeVariant {
	return value === "replica" ? "replica" : "classic";
}

function hasReplicaGroupShape(value: unknown): boolean {
	if (!isRecord(value)) {
		return false;
	}
	return (
		isRecord(value.utility) ||
		isRecord(value.logo) ||
		isRecord(value.panels) ||
		isRecord(value.behavior) ||
		isRecord(value.actions)
	);
}

export function isReplicaChrome(payload: {
	chromeVariant?: AdvancedMegaMenuChromeVariant;
	group?: unknown;
	replica?: unknown;
}): boolean {
	const withGroup = payload as AdvancedMegaMenuRuntimeWithGroup;
	if (withGroup.chromeVariant === "classic") {
		return false;
	}
	if (withGroup.chromeVariant === "replica") {
		return true;
	}
	if (hasReplicaGroupShape(withGroup.group)) {
		return true;
	}
	return hasReplicaGroupShape((payload as { replica?: unknown }).replica);
}

export function defaultReplicaChrome(): Partial<GroupMegaMenuHeaderRuntimePayload> {
	return seedReplicaGroupPartial();
}

export const DEFAULT_REPLICA_BRANDS: GroupHeaderBrand[] = [
	{
		id: "brand-ukpa",
		mono: "UK",
		name: "UK Property Accountants",
		tag: "Property tax specialists",
		accent: "#2E6BF6",
		accentDeep: "#0F3D91",
		accentSoft: "#E4EDFF",
		href: "/",
		ctaLabel: "Book a consultation",
		ctaHref: "#",
	},
	{
		id: "brand-rentalbux",
		mono: "RB",
		name: "RentalBux",
		tag: "MTD software for landlords",
		accent: "#1C8BE8",
		accentDeep: "#0A4A8A",
		accentSoft: "#E1F1FD",
		href: "/",
		ctaLabel: "Start free trial",
		ctaHref: "#",
	},
	{
		id: "brand-figsflow",
		mono: "FF",
		name: "FigsFlow",
		tag: "Proposals and engagement letters",
		accent: "#3B5BDB",
		accentDeep: "#1E2F8A",
		accentSoft: "#E6EAFB",
		href: "/",
		ctaLabel: "See a live demo",
		ctaHref: "#",
	},
	{
		id: "brand-crane",
		mono: "C&P",
		name: "Crane & Partners",
		tag: "Bromley accountants since 1910",
		accent: "#245FB0",
		accentDeep: "#0B2E63",
		accentSoft: "#E3ECF9",
		href: "/",
		ctaLabel: "Talk to a partner",
		ctaHref: "#",
		serif: true,
	},
];

export const DEFAULT_REPLICA_REGIONS: GroupHeaderRegion[] = [
	{
		code: "GB",
		name: "United Kingdom",
		langs: ["English"],
		timezoneNote: "Deadlines in UK time",
	},
	{
		code: "NP",
		name: "Nepal",
		langs: ["English", "नेपाली"],
		timezoneNote: "Support hours in Kathmandu time",
	},
	{
		code: "AE",
		name: "United Arab Emirates",
		langs: ["English", "العربية"],
		timezoneNote: "Non-resident landlord guidance",
	},
];

export function seedReplicaGroupPartial(): Partial<GroupMegaMenuHeaderRuntimePayload> {
	return {
		utility: {
			enabled: true,
			ticker: {
				enabled: true,
				text: "MTD for Income Tax is now live for landlords earning over £50,000.",
				linkLabel: "See what changes for you",
				href: "#",
			},
			links: [
				{ label: "Client login", href: "#" },
				{ label: "Careers", href: "#" },
			],
			regionSwitcher: {
				enabled: true,
				regions: DEFAULT_REPLICA_REGIONS,
				defaultRegionCode: "GB",
				defaultLangIndex: 0,
				currencyLabel: "GBP (£)",
			},
		},
		actions: {
			search: {
				enabled: true,
				style: "modal",
				placeholder: "Search 100+ pages…",
				shortcutEnabled: true,
				emptyMessage: "Nothing matches. Try another term.",
				maxResults: 10,
			},
			secondaryCta: { enabled: true, label: "Client login", href: "#" },
			primaryCta: { enabled: true, label: "Book a consultation", href: "#" },
			brandOverridesCta: true,
		},
		behavior: {
			sticky: true,
			stickyMode: "compact",
			compactScrollY: 24,
			barHeightPx: 76,
			compactBarHeightPx: 64,
			utilHeightPx: 38,
			zIndex: 100,
			glassBackground: true,
			glassBlurPx: 18,
			barShadow: true,
			openDelayMs: 120,
			closeDelayMs: 180,
			showNavCounts: true,
			navIconPosition: "left",
		},
		logo: {
			enabled: false,
			autoplayMs: 0,
			pauseOnHover: true,
			showNavButtons: true,
			showDots: true,
			stageWidthPx: 236,
			stageHeightPx: 48,
			brands: [],
		},
	};
}

function brandHasVisibleCopy(brand: GroupHeaderBrand): boolean {
	return Boolean(
		brand.name?.trim() ||
			brand.mono?.trim() ||
			brand.tag?.trim() ||
			brand.logoUrl?.trim() ||
			brand.logoMediaId?.trim()
	);
}

const SEED_REPLICA_BRAND_IDS = new Set(
	DEFAULT_REPLICA_BRANDS.map((brand) => brand.id)
);

function isUnauthoredSeedBrand(brand: GroupHeaderBrand): boolean {
	return (
		SEED_REPLICA_BRAND_IDS.has(brand.id) &&
		!(brand.logoUrl?.trim() || brand.logoMediaId?.trim())
	);
}

function usableReplicaBrands(
	brands: GroupHeaderBrand[] | undefined
): GroupHeaderBrand[] {
	return (brands ?? []).filter(
		(brand) => brandHasVisibleCopy(brand) && !isUnauthoredSeedBrand(brand)
	);
}

function isBlankReplicaPartial(
	partial: ReplicaChromeConfig | undefined
): boolean {
	if (!partial) {
		return true;
	}
	const ticker = partial.utility?.ticker?.text?.trim() ?? "";
	const links = partial.utility?.links?.length ?? 0;
	const brands = (partial.logo?.brands ?? []).filter(brandHasVisibleCopy);
	const utilOn = partial.utility?.enabled === true;
	const logoOn = partial.logo?.enabled === true;
	return !ticker && links === 0 && brands.length === 0 && !utilOn && !logoOn;
}

export function mergeReplicaGroupPartial(
	partial: ReplicaChromeConfig | undefined,
	roots: MenuRootNode[],
	menuId: string,
	menuName?: string
): GroupMegaMenuHeaderRuntimePayload {
	const seed = seedReplicaGroupPartial();
	const authored = isBlankReplicaPartial(partial)
		? {
				...seed,
				...(partial?.behavior
					? {
							behavior: {
								...seed.behavior,
								...partial.behavior,
							},
						}
					: {}),
			}
		: (partial ?? {});
	const base = mergeGroupMegaMenuHeaderPayload({
		...(seed as GroupMegaMenuHeaderPayloadPatch),
		...(authored as GroupMegaMenuHeaderPayloadPatch),
		menuId,
		menuName,
		roots,
	});
	const authoredRegions = authored.utility?.regionSwitcher?.regions;
	return mergeGroupMegaMenuHeaderPayload({
		...base,
		utility: {
			...base.utility,
			enabled: authored.utility?.enabled ?? base.utility.enabled,
			ticker: {
				...base.utility.ticker,
				enabled:
					authored.utility?.ticker?.enabled ?? base.utility.ticker.enabled,
				text: authored.utility?.ticker?.text ?? base.utility.ticker.text,
				href: authored.utility?.ticker?.href ?? base.utility.ticker.href,
				linkLabel:
					authored.utility?.ticker?.linkLabel ?? base.utility.ticker.linkLabel,
			},
			links: authored.utility?.links
				? [...authored.utility.links]
				: base.utility.links,
			regionSwitcher: {
				...base.utility.regionSwitcher,
				enabled:
					authored.utility?.regionSwitcher?.enabled ??
					base.utility.regionSwitcher.enabled,
				regions:
					authoredRegions && authoredRegions.length > 0
						? [...authoredRegions]
						: base.utility.regionSwitcher.regions.length > 0
							? base.utility.regionSwitcher.regions
							: DEFAULT_REPLICA_REGIONS,
				defaultRegionCode:
					authored.utility?.regionSwitcher?.defaultRegionCode ??
					base.utility.regionSwitcher.defaultRegionCode,
				defaultLangIndex:
					authored.utility?.regionSwitcher?.defaultLangIndex ??
					base.utility.regionSwitcher.defaultLangIndex,
				currencyLabel:
					authored.utility?.regionSwitcher?.currencyLabel ??
					base.utility.regionSwitcher.currencyLabel,
			},
		},
		logo: {
			...base.logo,
			enabled: authored.logo?.enabled ?? false,
			autoplayMs: authored.logo?.autoplayMs ?? base.logo.autoplayMs,
			pauseOnHover: authored.logo?.pauseOnHover ?? base.logo.pauseOnHover,
			showNavButtons: authored.logo?.showNavButtons ?? base.logo.showNavButtons,
			showDots: authored.logo?.showDots ?? base.logo.showDots,
			stageWidthPx: authored.logo?.stageWidthPx ?? base.logo.stageWidthPx,
			stageHeightPx: authored.logo?.stageHeightPx ?? base.logo.stageHeightPx,
			brands: usableReplicaBrands(
				authored.logo?.brands ? [...authored.logo.brands] : undefined
			),
		},
		actions: {
			...base.actions,
			search: {
				...base.actions.search,
				enabled:
					authored.actions?.search?.enabled ?? base.actions.search.enabled,
				style: authored.actions?.search?.style ?? base.actions.search.style,
				placeholder:
					authored.actions?.search?.placeholder ??
					base.actions.search.placeholder,
				shortcutEnabled:
					authored.actions?.search?.shortcutEnabled ??
					base.actions.search.shortcutEnabled,
				emptyMessage:
					authored.actions?.search?.emptyMessage ??
					base.actions.search.emptyMessage,
				maxResults:
					authored.actions?.search?.maxResults ??
					base.actions.search.maxResults,
			},
			secondaryCta: {
				enabled:
					authored.actions?.secondaryCta?.enabled ??
					base.actions.secondaryCta.enabled,
				label:
					authored.actions?.secondaryCta?.label ??
					base.actions.secondaryCta.label,
				href:
					authored.actions?.secondaryCta?.href ??
					base.actions.secondaryCta.href,
			},
			primaryCta: {
				enabled:
					authored.actions?.primaryCta?.enabled ??
					base.actions.primaryCta.enabled,
				label:
					authored.actions?.primaryCta?.label ?? base.actions.primaryCta.label,
				href:
					authored.actions?.primaryCta?.href ?? base.actions.primaryCta.href,
			},
			brandOverridesCta:
				authored.actions?.brandOverridesCta ?? base.actions.brandOverridesCta,
		},
		behavior: {
			...base.behavior,
			...(authored.behavior ?? {}),
			sticky:
				typeof authored.behavior?.sticky === "boolean"
					? authored.behavior.sticky
					: base.behavior.sticky,
			stickyMode: authored.behavior?.stickyMode ?? base.behavior.stickyMode,
			showNavCounts:
				authored.behavior?.showNavCounts ?? base.behavior.showNavCounts,
		},
	});
}

export function collectLinkedSubcategoryMenuIds(
	panels: Record<string, GroupHeaderPanelConfig> | undefined
): string[] {
	const ids: string[] = [];
	for (const panel of Object.values(panels ?? {})) {
		if (
			panel.subcategoryMode === "linked_menu" &&
			typeof panel.subcategoryMenuId === "string" &&
			panel.subcategoryMenuId.trim()
		) {
			ids.push(panel.subcategoryMenuId.trim());
		}
	}
	return [...new Set(ids)];
}

/** Copy the header branding/override logo onto carousel tiles that have none. */
export function fillReplicaBrandLogoUrls(
	brands: GroupHeaderBrand[],
	logoUrl: string | undefined
): GroupHeaderBrand[] {
	const src = logoUrl?.trim();
	if (!src) {
		return brands;
	}
	return brands.map((brand) =>
		brand.logoUrl?.trim() ? brand : { ...brand, logoUrl: src }
	);
}

export function overlayLinkedMenuRoots(
	roots: MenuRootNode[],
	panels: Record<string, GroupHeaderPanelConfig> | undefined,
	menusById: Record<string, MenuRootNode[]>
): MenuRootNode[] {
	if (!panels) {
		return roots;
	}
	return roots.map((root) => {
		if (root.itemKind !== "mega_tab") {
			return root;
		}
		const panel = panels[root.id];
		if (panel?.subcategoryMode !== "linked_menu") {
			return root;
		}
		const menuId = panel.subcategoryMenuId?.trim();
		if (!menuId) {
			return root;
		}
		const live = menusById[menuId];
		if (!live?.length) {
			return root;
		}
		const donor = live.find((node) => node.itemKind === "mega_tab") ?? live[0];
		if (!donor || donor.itemKind !== "mega_tab") {
			return root;
		}
		return {
			...root,
			groups: sortMenuMegaGroups(donor.groups),
			columnCount: donor.columnCount,
			megaMenuId: donor.megaMenuId,
			megaMenuContent: donor.megaMenuContent,
		};
	});
}

function localesToRegions(
	languageSwitcher: MegaMenuHeaderLanguageSwitcher
): GroupHeaderRegion[] {
	return languageSwitcher.locales.map((loc) => ({
		code: loc.code,
		name: loc.label,
		langs: [loc.label],
		timezoneNote: "",
	}));
}

export function buildGroupHeaderFromAdvancedElement(
	el: AdvancedMegaMenuHeaderElement,
	roots: MenuRootNode[]
): GroupMegaMenuHeaderRuntimePayload {
	const merged = mergeReplicaGroupPartial(
		el.group ?? el.replica,
		roots,
		el.menuId,
		el.menuName
	);
	const withActions = mergeGroupMegaMenuHeaderPayload({
		...merged,
		actions: {
			...merged.actions,
			secondaryCta: {
				enabled: el.contact.enabled,
				label: el.contact.label,
				href: el.contact.href,
			},
			primaryCta: {
				enabled: el.cta.enabled,
				label: el.cta.label,
				href: el.cta.href,
			},
		},
		behavior: {
			...merged.behavior,
			closeDelayMs: el.group?.behavior?.closeDelayMs ?? el.closeDelayMs,
			sticky: el.group?.behavior?.sticky ?? el.sticky ?? true,
		},
	});

	const regions = withActions.utility.regionSwitcher.regions;
	if (regions.length === 0 && el.languageSwitcher.enabled) {
		return {
			...withActions,
			utility: {
				...withActions.utility,
				regionSwitcher: {
					...withActions.utility.regionSwitcher,
					enabled: withActions.utility.regionSwitcher.enabled,
					regions: localesToRegions(el.languageSwitcher),
					defaultRegionCode:
						withActions.utility.regionSwitcher.defaultRegionCode ||
						el.languageSwitcher.defaultCode,
				},
			},
		};
	}
	return withActions;
}

export function groupFromPayload(
	payload: AdvancedMegaMenuHeaderRuntimePayload
): GroupMegaMenuHeaderRuntimePayload {
	const withGroup = payload as AdvancedMegaMenuRuntimeWithGroup;
	return mergeGroupMegaMenuHeaderPayload({
		...withGroup.group,
		menuId: payload.menuId,
		menuName: payload.menuName,
		roots: payload.roots,
	});
}

type MegaTabRoot = Extract<MenuRootNode, { itemKind: "mega_tab" }>;

export function countMegaTabEntries(
	root: MegaTabRoot,
	panel?: GroupHeaderPanelConfig,
	moreLinks?: Record<string, GroupHeaderGroupMoreLink>
): number {
	const groups = resolvePanelGroups(root, panel, moreLinks);
	let n = 0;
	for (const group of groups) {
		n += group.items.length;
		if (group.more?.href?.trim()) {
			n += 1;
		}
	}
	for (const product of panel?.products ?? []) {
		n += product.links.length;
	}
	if (panel?.deadlines?.enabled) {
		n += panel.deadlines.items.length;
		if (panel.deadlines.moreHref?.trim()) {
			n += 1;
		}
	}
	return n;
}

export type ReplicaSearchHit = {
	group: string;
	href: string;
	label: string;
};

export function buildReplicaSearchIndex(
	roots: MenuRootNode[],
	panels?: Record<string, GroupHeaderPanelConfig>,
	moreLinks?: Record<string, GroupHeaderGroupMoreLink>
): ReplicaSearchHit[] {
	const hits: ReplicaSearchHit[] = [];
	for (const root of roots) {
		if (root.itemKind === "link") {
			hits.push({
				label: root.label,
				href: root.href,
				group: root.label,
			});
			continue;
		}
		if (root.itemKind !== "mega_tab") {
			continue;
		}
		const panel = panels?.[root.id];
		const groups = resolvePanelGroups(root, panel, moreLinks);
		for (const group of groups) {
			for (const item of group.items) {
				hits.push({
					label: item.label,
					href: item.href,
					group: `${root.label} / ${group.label}`,
				});
			}
		}
		for (const product of panel?.products ?? []) {
			for (const link of product.links) {
				hits.push({
					label: link.label,
					href: link.href,
					group: `${root.label} / ${product.name}`,
				});
			}
		}
	}
	return hits;
}

/** Whole calendar days until `isoDate`. Negative when the date is already past. */
export function daysUntilIso(isoDate: string, now = Date.now()): number {
	const t = Date.parse(isoDate);
	if (!Number.isFinite(t)) {
		return 0;
	}
	return Math.ceil((t - now) / 86_400_000);
}

export function replicaGridClassName(
	layout: GroupHeaderMegaPanelLayout | undefined
): "dense" | "products" | "resources" | "company" | "tabs" {
	if (layout === "product_cards_groups") {
		return "products";
	}
	if (layout === "dense_columns_deadlines") {
		return "resources";
	}
	if (layout === "dense_columns") {
		return "company";
	}
	if (layout === "left_tabs") {
		return "tabs";
	}
	return "dense";
}

export function replicaIsLeftTabsLayout(
	layout: GroupHeaderMegaPanelLayout | undefined
): boolean {
	return layout === "left_tabs";
}

/** Right-rail column is featured only. Deadlines live in the panel footer row. */
export function replicaPanelUsesAsideRail(
	showFeatured: boolean,
	_showDeadlines?: boolean
): boolean {
	return showFeatured;
}

/** Deadlines no longer stack under featured — keep the helper so callers stay stable. */
export function replicaPanelStacksAsideBlocks(
	_showFeatured: boolean,
	_showDeadlines: boolean
): boolean {
	return false;
}

export function replicaShowsFeaturedAside(
	layout: GroupHeaderMegaPanelLayout | undefined,
	featured: GroupHeaderFeaturedCard | undefined
): boolean {
	if (featured?.enabled === false) {
		return false;
	}
	if (featured?.contentMode === "html") {
		return Boolean(featured.html?.trim());
	}
	const hasCopy = Boolean(
		featured?.title?.trim() ||
			featured?.label?.trim() ||
			featured?.body?.trim() ||
			featured?.metric?.trim()
	);
	if (featured?.enabled === true) {
		return true;
	}
	return layout === "dense_columns_featured" && hasCopy;
}

export function replicaShowsDeadlinesAside(
	layout: GroupHeaderMegaPanelLayout | undefined,
	enabled: boolean | undefined,
	itemCount: number
): boolean {
	if (enabled === false) {
		return false;
	}
	if (enabled === true) {
		return itemCount > 0;
	}
	return layout === "dense_columns_deadlines" && itemCount > 0;
}

export function replicaStatusSitsInGrid(
	layout: GroupHeaderMegaPanelLayout | undefined
): boolean {
	return layout === "product_cards_groups";
}

export function formatReplicaDeadlineCaption(
	isoDate: string,
	subtitle: string
): string {
	const dateLabel = isoDate
		? new Date(isoDate).toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "short",
				year: "numeric",
			})
		: "";
	const sub = subtitle.trim();
	if (sub && dateLabel && !sub.includes(dateLabel)) {
		return `${sub}, ${dateLabel}`;
	}
	return sub || dateLabel;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function layoutFromLegacyKind(kind: unknown): GroupHeaderMegaPanelLayout {
	return parseGroupHeaderMegaPanelLayout(kind);
}

function legacyReplicaToGroup(
	raw: Record<string, unknown>
): Partial<GroupMegaMenuHeaderRuntimePayload> {
	const panels: Record<string, GroupHeaderPanelConfig> = {};
	if (isRecord(raw.panelLayouts)) {
		for (const [tabId, layout] of Object.entries(raw.panelLayouts)) {
			if (!isRecord(layout)) {
				continue;
			}
			panels[tabId] = {
				layout: layoutFromLegacyKind(layout.kind),
				subcategoryMode: "menu_groups",
				footer: {
					enabled: true,
					message:
						typeof layout.footerMessage === "string"
							? layout.footerMessage
							: "",
					links: [],
					ctas: [],
				},
				status:
					typeof layout.statusLabel === "string" && layout.statusLabel
						? {
								enabled: true,
								label: layout.statusLabel,
								updatedLabel: "",
							}
						: undefined,
			};
		}
	}
	const ticker = isRecord(raw.ticker) ? raw.ticker : {};
	const brands = Array.isArray(raw.brands)
		? (raw.brands as GroupHeaderBrand[])
		: [];
	const links = Array.isArray(raw.utilityLinks)
		? raw.utilityLinks.filter(isRecord).map((link) => ({
				label: typeof link.label === "string" ? link.label : "",
				href: typeof link.href === "string" ? link.href : "#",
			}))
		: [];
	return {
		utility: {
			enabled: raw.utilityStripEnabled !== false,
			ticker: {
				enabled: ticker.enabled !== false,
				text: typeof ticker.text === "string" ? ticker.text : "",
				linkLabel: typeof ticker.linkLabel === "string" ? ticker.linkLabel : "",
				href: typeof ticker.href === "string" ? ticker.href : "#",
			},
			links,
			regionSwitcher: {
				enabled: false,
				regions: [],
				defaultRegionCode: "",
				defaultLangIndex: 0,
				currencyLabel: "",
			},
		},
		actions: {
			search: {
				enabled: raw.searchEnabled !== false,
				style: "modal",
				placeholder:
					typeof raw.searchPlaceholder === "string"
						? raw.searchPlaceholder
						: "Search…",
				shortcutEnabled: true,
				emptyMessage: "Nothing matches. Try another term.",
				maxResults: 10,
			},
			secondaryCta: { enabled: false, href: "#", label: "" },
			primaryCta: { enabled: false, href: "#", label: "" },
			brandOverridesCta: false,
		},
		behavior: {
			sticky: true,
			stickyMode: "compact",
			compactScrollY: 24,
			barHeightPx: 76,
			compactBarHeightPx: 64,
			utilHeightPx: 38,
			zIndex: 100,
			glassBackground: true,
			glassBlurPx: 18,
			barShadow: true,
			openDelayMs: 120,
			closeDelayMs: 180,
			showNavCounts: raw.showNavCounts !== false,
			navIconPosition: "left",
		},
		logo: {
			enabled: brands.length > 0,
			autoplayMs:
				typeof raw.brandRotateMs === "number" ? raw.brandRotateMs : 4200,
			pauseOnHover: true,
			showNavButtons: true,
			showDots: true,
			stageWidthPx: 236,
			stageHeightPx: 48,
			brands,
		},
		panels,
		linkBadges: isRecord(raw.linkBadges)
			? (raw.linkBadges as GroupMegaMenuHeaderRuntimePayload["linkBadges"])
			: {},
	};
}

/** Persistable group JSON on the advanced element (`group`, not a new GE type). */
export function sanitizeReplicaChrome(
	raw: unknown
): GroupMegaMenuHeaderPayloadPatch | undefined {
	if (!isRecord(raw)) {
		return undefined;
	}
	if ("utilityStripEnabled" in raw || "panelLayouts" in raw) {
		return legacyReplicaToGroup(raw);
	}
	return raw as GroupMegaMenuHeaderPayloadPatch;
}

function parseReplicaStickyMode(value: unknown): GroupHeaderStickyMode | null {
	if (value === "always" || value === "smart" || value === "compact") {
		return value;
	}
	return null;
}

/**
 * Replica chrome reads `group.behavior` first. Element `sticky` is the
 * classic-header field — use it only when group JSON omitted the flag.
 */
export function resolveReplicaStickyBehavior(
	rawBehavior: Partial<GroupHeaderBehavior> | null | undefined,
	payloadSticky: boolean | undefined,
	payloadStickyMode: string | undefined,
	merged: GroupHeaderBehavior
): Pick<GroupHeaderBehavior, "sticky" | "stickyMode"> {
	const rawMode = parseReplicaStickyMode(rawBehavior?.stickyMode);
	return {
		sticky:
			typeof rawBehavior?.sticky === "boolean"
				? rawBehavior.sticky
				: payloadSticky === true
					? true
					: merged.sticky,
		stickyMode:
			rawMode ?? (payloadStickyMode === "smart" ? "smart" : merged.stickyMode),
	};
}

/**
 * Replica defaults sticky on. Classic pill chrome only pins when
 * `element.sticky === true` (omitted sticky stays in-flow).
 */
export function replicaElementWantsSticky(el: {
	chromeVariant?: AdvancedMegaMenuChromeVariant;
	group?: unknown;
	replica?: unknown;
	sticky?: boolean;
}): boolean {
	if (isReplicaChrome(el)) {
		const raw = isRecord(el.group)
			? el.group
			: isRecord(el.replica)
				? el.replica
				: null;
		const sticky = raw && isRecord(raw.behavior) ? raw.behavior.sticky : null;
		if (typeof sticky === "boolean") {
			return sticky;
		}
		return true;
	}
	return el.sticky === true;
}

function walkBuilderNodes(
	nodes: unknown,
	visit: (node: Record<string, unknown>) => void
): void {
	if (!Array.isArray(nodes)) {
		return;
	}
	for (const node of nodes) {
		if (!isRecord(node)) {
			continue;
		}
		visit(node);
		walkBuilderNodes(node.children, visit);
	}
}

function replicaStickyFromMegaPayloadJson(json: unknown): boolean | null {
	if (typeof json !== "string" || json.length === 0) {
		return null;
	}
	try {
		const payload: unknown = JSON.parse(json);
		if (!(isRecord(payload) && isReplicaChrome(payload))) {
			return null;
		}
		return replicaElementWantsSticky(payload);
	} catch {
		return null;
	}
}

/**
 * Public Region: replica sticky cannot use CSS `position:sticky` when page CSS
 * sets `overflow-x:hidden` on `html,body` (that computes `overflow-y:auto` and
 * makes them the sticky containing block). Returns whether the header builder
 * doc should use a viewport-fixed pin instead.
 *
 * `true` — replica chrome with sticky on (default).
 * `false` — replica chrome with sticky explicitly off.
 * `null` — no replica header in the tree (classic / other chrome).
 */
export function readBuilderDocReplicaSticky(doc: unknown): boolean | null {
	if (!isRecord(doc)) {
		return null;
	}
	let found: boolean | null = null;
	walkBuilderNodes(doc.root, (node) => {
		const props = isRecord(node.props) ? node.props : null;
		if (!props) {
			return;
		}
		const isAdvanced = props.__builderAdvancedMegaMenuHeader === true;
		const isReplicaFlag = props.__builderReplicaMegaMenuHeader === true;
		if (!(isAdvanced || isReplicaFlag)) {
			return;
		}
		const fromJson =
			replicaStickyFromMegaPayloadJson(props.__megaMenuPayloadJson) ??
			replicaStickyFromMegaPayloadJson(props.__replicaMegaMenuPayloadJson);
		if (fromJson != null) {
			found = fromJson;
			return;
		}
		if (isReplicaFlag) {
			found = true;
		}
	});
	return found;
}

export function replicaStickyShellState(
	behavior: GroupHeaderBehavior,
	stickyUi: { retracted: boolean; stuck: boolean }
): { compact: boolean; retracted: boolean; sticky: boolean } {
	const sticky = behavior.sticky === true;
	return {
		sticky,
		compact: sticky && behavior.stickyMode === "compact" && stickyUi.stuck,
		retracted: sticky && behavior.stickyMode === "smart" && stickyUi.retracted,
	};
}

export function replicaFromPayload(
	payload: AdvancedMegaMenuHeaderRuntimePayload
): GroupMegaMenuHeaderRuntimePayload {
	const withGroup = payload as AdvancedMegaMenuRuntimeWithGroup;
	const nested = withGroup.group;
	const legacy = sanitizeReplicaChrome(
		(payload as { replica?: unknown }).replica
	);
	const rawPartial = { ...legacy, ...nested } as ReplicaChromeConfig;
	const merged = mergeReplicaGroupPartial(
		rawPartial,
		payload.roots,
		payload.menuId,
		payload.menuName
	);
	const stickyFields = resolveReplicaStickyBehavior(
		rawPartial.behavior,
		payload.sticky,
		payload.stickyMode,
		merged.behavior
	);
	const contactLooksDefault =
		payload.contact.label === "Contact us" &&
		payload.contact.href === "#contact";
	const ctaLooksDefault =
		payload.cta.label === "Get Started" && payload.cta.href === "#start";
	return mergeGroupMegaMenuHeaderPayload({
		...merged,
		behavior: {
			...merged.behavior,
			...stickyFields,
		},
		actions: {
			...merged.actions,
			secondaryCta: contactLooksDefault
				? merged.actions.secondaryCta
				: {
						enabled: payload.contact.enabled,
						label: payload.contact.label,
						href: payload.contact.href,
					},
			primaryCta: ctaLooksDefault
				? merged.actions.primaryCta
				: {
						enabled: payload.cta.enabled,
						label: payload.cta.label,
						href: payload.cta.href,
					},
		},
	});
}

export function resolvePanelGroups(
	root: Extract<MenuRootNode, { itemKind: "mega_tab" }>,
	panel: GroupHeaderPanelConfig | undefined,
	moreLinks?: Record<string, GroupHeaderGroupMoreLink>
): GroupHeaderDenseGroup[] {
	const inline =
		panel?.subcategoryMode === "inline_groups" &&
		Array.isArray(panel.inlineGroups) &&
		panel.inlineGroups.length > 0;
	if (inline && panel.inlineGroups) {
		return panel.inlineGroups;
	}
	return sortMenuMegaGroups(root.groups).map((group) => {
		const authoredMore = moreLinks?.[group.id];
		const href = group.href?.trim();
		const more =
			authoredMore ??
			(href && href !== "#" ? { href, label: `All ${group.label}` } : null);
		return {
			id: group.id,
			label: group.label,
			href: group.href,
			items: group.links.map((link) => ({
				id: link.id,
				label: link.label,
				href: link.href,
				openInNewTab: link.openInNewTab,
			})),
			more,
		};
	});
}

function navIconUsable(
	icon: AdvancedMegaMenuNavIcon | undefined
): AdvancedMegaMenuNavIcon | undefined {
	if (!icon?.svgMarkup?.trim()) {
		return undefined;
	}
	return icon;
}

/** Direct id first, then same-label menu group/link ids (inline columns vs menu tree). */
export function lookupNavIcon(
	navIcons: Record<string, AdvancedMegaMenuNavIcon> | undefined,
	itemId: string | undefined,
	aliasIds: readonly string[] = []
): AdvancedMegaMenuNavIcon | undefined {
	if (!navIcons) {
		return undefined;
	}
	const hit = navIconUsable(itemId ? navIcons[itemId] : undefined);
	if (hit) {
		return hit;
	}
	for (const aliasId of aliasIds) {
		const aliased = navIconUsable(navIcons[aliasId]);
		if (aliased) {
			return aliased;
		}
	}
	return undefined;
}

export function replicaGroupIconAliasIds(
	root: Extract<MenuRootNode, { itemKind: "mega_tab" }>,
	group: Pick<GroupHeaderDenseGroup, "id" | "label">
): string[] {
	const needle = group.label.trim().toLowerCase();
	if (!needle) {
		return [];
	}
	return root.groups
		.filter(
			(menuGroup) =>
				menuGroup.id !== group.id &&
				menuGroup.label.trim().toLowerCase() === needle
		)
		.map((menuGroup) => menuGroup.id);
}

export function replicaLinkIconAliasIds(
	root: Extract<MenuRootNode, { itemKind: "mega_tab" }>,
	link: { id: string; label: string }
): string[] {
	const needle = link.label.trim().toLowerCase();
	if (!needle) {
		return [];
	}
	const aliases: string[] = [];
	for (const menuGroup of root.groups) {
		for (const menuLink of menuGroup.links) {
			if (
				menuLink.id !== link.id &&
				menuLink.label.trim().toLowerCase() === needle
			) {
				aliases.push(menuLink.id);
			}
		}
	}
	return aliases;
}

export function replicaLayoutFlags(
	widthPx: number,
	breakpoints: GroupHeaderBreakpoints
): { asideHide: boolean; drawer: boolean; narrow: boolean } {
	return {
		asideHide: widthPx <= breakpoints.megaAsideHidePx,
		drawer: widthPx < breakpoints.drawerPx,
		narrow: widthPx < breakpoints.compactCtaHidePx,
	};
}

/** Extra px the bar must grow before leaving overflow-driven burger (avoids CTA hide/show flicker). */
export const REPLICA_NAV_OVERFLOW_HYSTERESIS_PX = 48;

/**
 * Sum visible bar-row children. For the primary `<nav>`, use scroll width so
 * flex-shrink on `.nav` cannot under-report clipped labels (root cause of
 * overlap + burger/desktop flicker on narrow desktop widths).
 */
export function measureReplicaBarRowNeededPx(
	wrap: HTMLElement,
	nav: HTMLElement | null
): number {
	const style = window.getComputedStyle(wrap);
	const gapRaw = style.columnGap === "normal" ? style.gap : style.columnGap;
	const gap = Number.parseFloat(gapRaw) || 0;
	const kids: HTMLElement[] = [];
	for (const node of wrap.children) {
		if (!(node instanceof HTMLElement)) {
			continue;
		}
		if (window.getComputedStyle(node).display === "none") {
			continue;
		}
		kids.push(node);
	}
	if (kids.length === 0) {
		return 0;
	}
	let sum = 0;
	for (const kid of kids) {
		if (nav && kid === nav) {
			sum += Math.max(kid.offsetWidth, kid.scrollWidth);
		} else {
			sum += kid.offsetWidth;
		}
	}
	return sum + gap * (kids.length - 1);
}

/**
 * Desktop nav stays nowrap; when labels would clip or the bar row would
 * collide with the logo, switch to the existing burger — not only `drawerPx`.
 * While compact-from-overflow, compare the container to the last desktop
 * `neededPx` so hiding `.nav` / ghost CTA cannot immediately restore desktop.
 */
export function replicaBarOverflowState({
	cachedNeededPx,
	compactFromOverflow,
	hysteresisPx = REPLICA_NAV_OVERFLOW_HYSTERESIS_PX,
	navClientWidth,
	navScrollWidth,
	rowNeededPx,
	wrapClientWidth,
}: {
	cachedNeededPx: number;
	compactFromOverflow: boolean;
	hysteresisPx?: number;
	navClientWidth: number;
	navScrollWidth: number;
	rowNeededPx: number;
	wrapClientWidth: number;
}): { neededPx: number; overflow: boolean } {
	if (compactFromOverflow) {
		return {
			neededPx: cachedNeededPx,
			overflow: wrapClientWidth < cachedNeededPx + hysteresisPx,
		};
	}
	const overflow =
		navScrollWidth > navClientWidth + 1 || rowNeededPx > wrapClientWidth + 1;
	return {
		neededPx: Math.max(rowNeededPx, cachedNeededPx),
		overflow,
	};
}

/**
 * Canvas device toolbar: desktop keeps hover mega; tablet and mobile use the
 * drawer (drawer breakpoint is typically 1024px, so tablet is compact).
 */
export function replicaPreviewShowsDrawer(
	forced: "mobile" | "tablet" | "desktop" | null | undefined
): boolean {
	return forced === "mobile" || forced === "tablet";
}

/** Canvas: force full desktop chrome only when the toolbar is Desktop. */
export function replicaForceDesktopPreview(
	previewMode: boolean,
	forced: "mobile" | "tablet" | "desktop" | null | undefined
): boolean {
	if (!previewMode) {
		return false;
	}
	return !replicaPreviewShowsDrawer(forced);
}

export function replicaForcedWidthPx(
	forced: "mobile" | "tablet" | "desktop" | null | undefined,
	breakpoints: GroupHeaderBreakpoints
): number | null {
	if (forced === "desktop") {
		return Math.max(breakpoints.megaAsideHidePx, breakpoints.drawerPx) + 80;
	}
	if (forced === "tablet") {
		return Math.min(768, Math.max(breakpoints.drawerPx - 1, 320));
	}
	if (forced === "mobile") {
		return Math.min(breakpoints.compactCtaHidePx, 390);
	}
	return null;
}

export function applyGroupHeaderCssVars(
	design: GroupHeaderDesignTokens,
	behavior: GroupMegaMenuHeaderRuntimePayload["behavior"],
	typography: GroupMegaMenuHeaderRuntimePayload["typography"],
	breakpoints: GroupMegaMenuHeaderRuntimePayload["breakpoints"],
	extras?: {
		accent?: string;
		accentDeep?: string;
		accentSoft?: string;
		brandDotMs?: number;
		drawerColumns?: number;
		drawerWidthPx?: number;
		logoHeightPx?: number;
		logoWidthPx?: number;
	}
): Record<string, string> {
	const tokens = resolveGroupHeaderDesign(design);
	const theme = GROUP_HEADER_THEME_TOKEN_DEFAULTS;
	const legacy = LEGACY_GROUP_HEADER_HEX;
	const accent = resolveGroupHeaderColor(
		extras?.accent?.trim() || tokens.electric,
		theme.electric,
		legacy.electric
	);
	const accentDeep = resolveGroupHeaderColor(
		extras?.accentDeep?.trim() || tokens.royal,
		theme.royal,
		legacy.royal
	);
	const accentSoft = resolveGroupHeaderColor(
		extras?.accentSoft?.trim() || tokens.ice,
		theme.ice,
		legacy.accentSoft
	);
	const regionPopBackground = isInheritedGroupHeaderColor(
		design.regionPopBackground,
		theme.regionPopBackground,
		legacy.regionPopBackground
	)
		? tokens.megaBackground
		: tokens.regionPopBackground;
	const regionPopHeading = isInheritedGroupHeaderColor(
		design.regionPopHeading,
		theme.regionPopHeading,
		legacy.regionPopHeading
	)
		? theme.regionPopHeading
		: tokens.regionPopHeading;
	const regionItemText = isInheritedGroupHeaderColor(
		design.regionItemText,
		theme.regionItemText,
		legacy.regionItemText
	)
		? tokens.ink
		: tokens.regionItemText;
	const regionItemSelectedBackground = isInheritedGroupHeaderColor(
		design.regionItemSelectedBackground,
		theme.regionItemSelectedBackground,
		legacy.regionItemSelectedBackground
	)
		? accentSoft
		: tokens.regionItemSelectedBackground;
	const regionItemSelectedText = isInheritedGroupHeaderColor(
		design.regionItemSelectedText,
		theme.regionItemSelectedText,
		legacy.regionItemSelectedText
	)
		? accentDeep
		: tokens.regionItemSelectedText;
	const navItemHoverText = isInheritedGroupHeaderColor(
		design.navItemHoverText,
		theme.navItemHoverText,
		legacy.navItemHoverText
	)
		? accentDeep
		: tokens.navItemHoverText;
	const navItemHoverBackground = isInheritedGroupHeaderColor(
		design.navItemHoverBackground,
		theme.navItemHoverBackground,
		legacy.navItemHoverBackground
	)
		? "transparent"
		: tokens.navItemHoverBackground;
	const megaLinkHoverText = isInheritedGroupHeaderColor(
		design.megaLinkHoverText,
		theme.megaLinkHoverText,
		legacy.megaLinkHoverText
	)
		? accentDeep
		: tokens.megaLinkHoverText;
	const megaLinkHoverBackground = isInheritedGroupHeaderColor(
		design.megaLinkHoverBackground,
		theme.megaLinkHoverBackground,
		legacy.megaLinkHoverBackground
	)
		? tokens.ice
		: tokens.megaLinkHoverBackground;
	return {
		"--ghh-navy": tokens.navy,
		"--ghh-royal": tokens.royal,
		"--ghh-electric": tokens.electric,
		"--ghh-sky": tokens.sky,
		"--ghh-ice": tokens.ice,
		"--ghh-ink": tokens.ink,
		"--ghh-link": tokens.ink,
		"--ghh-cta-primary-fg": tokens.primaryCtaText,
		"--ghh-acc-fg": tokens.primaryCtaText,
		"--ghh-cta-ghost-fg": tokens.ghostCtaText,
		"--ghh-mute": tokens.mute,
		"--ghh-line": tokens.line,
		"--ghh-line-2": tokens.lineSubtle,
		"--ghh-acc": accent,
		"--ghh-acc-deep": accentDeep,
		"--ghh-acc-soft": accentSoft,
		"--ghh-hdr-bg": tokens.headerBackground,
		"--ghh-mega-bg": tokens.megaBackground,
		"--ghh-hdr-border": tokens.line,
		"--ghh-hdr-shadow": behavior.barShadow
			? "0 10px 40px -20px color-mix(in srgb, var(--ghh-navy) 35%, transparent)"
			: "none",
		"--ghh-blur": behavior.glassBackground
			? `saturate(1.4) blur(${Math.min(48, Math.max(0, behavior.glassBlurPx))}px)`
			: "none",
		"--ghh-util-bg": tokens.utilBackground,
		"--ghh-util-fg": tokens.utilText,
		"--ghh-util-link": tokens.utilLink,
		"--ghh-util-link-underline": `color-mix(in srgb, ${tokens.utilLink} 35%, transparent)`,
		"--ghh-font": typography.fontFamily,
		"--ghh-nav-fs": `${typography.navFontSizePx}px`,
		"--ghh-nav-fw": String(typography.navFontWeight),
		"--ghh-link-fs": `${typography.linkFontSizePx}px`,
		"--ghh-grp-title-fs": `${typography.groupTitleFontSizePx}px`,
		"--ghh-util-fs": `${typography.utilFontSizePx}px`,
		"--ghh-hdr-h": `${behavior.barHeightPx}px`,
		"--ghh-hdr-h-compact": `${behavior.compactBarHeightPx}px`,
		"--ghh-util-h": `${behavior.utilHeightPx}px`,
		"--ghh-z": String(behavior.zIndex),
		"--ghh-wrap-max": `${design.maxWidthPx}px`,
		"--ghh-wrap-pad": `${design.wrapPaddingPx}px`,
		"--ghh-radius-md": design.radiusMd,
		"--ghh-radius-lg": design.radiusLg,
		"--ghh-drawer-bp": `${breakpoints.drawerPx}px`,
		"--ghh-aside-bp": `${breakpoints.megaAsideHidePx}px`,
		"--ghh-narrow-bp": `${breakpoints.compactCtaHidePx}px`,
		"--ghh-drawer-w": `${extras?.drawerWidthPx ?? 440}px`,
		"--ghh-logo-w": `${extras?.logoWidthPx ?? 236}px`,
		"--ghh-logo-h": `${extras?.logoHeightPx ?? 48}px`,
		"--ghh-dot-ms": `${extras?.brandDotMs ?? 4200}ms`,
		"--ghh-drawer-cols": String(extras?.drawerColumns ?? 2),
		"--ghh-region-pop-bg": regionPopBackground,
		"--ghh-region-pop-heading": regionPopHeading,
		"--ghh-region-item-fg": regionItemText,
		"--ghh-region-item-selected-bg": regionItemSelectedBackground,
		"--ghh-region-item-selected-fg": regionItemSelectedText,
		"--ghh-nav-hover-fg": navItemHoverText,
		"--ghh-nav-hover-bg": navItemHoverBackground,
		"--ghh-link-hover-fg": megaLinkHoverText,
		"--ghh-link-hover-bg": megaLinkHoverBackground,
	};
}
